// Pure resolver for the Discord deep-link selection on /inbox.
//
// The Discord bot's "Open in Reverra" button posts a URL with a
// `#approval=<uuid>` fragment (see apps/workers/src/agents/discord/embeds.ts).
// Inbox.tsx reads this on first load to pre-select the matching pending
// row, so the owner lands directly on the row that triggered the
// Discord notification instead of the default first-row selection.
//
// Extracted to a pure function so it can be unit-tested without mounting
// the full Inbox page + React Query + supabase stack.

/** Strict UUID v4-ish shape: 8-4-4-4-12 hex with dashes. */
const APPROVAL_HASH_PATTERN = /^#approval=([0-9a-f-]{36})$/i;

/**
 * Extract the `#approval=<uuid>` id from a location hash, or null
 * when the hash is absent / malformed. Exposed so the in-page
 * "explicit hash wins" effect can re-run on each navigation without
 * re-implementing the regex.
 */
export function parseApprovalHash(hash: string): string | null {
  const m = APPROVAL_HASH_PATTERN.exec(hash);
  return m && m[1] ? m[1] : null;
}

/**
 * Resolve which pending-approval id to pre-select on first load.
 *
 * - Returns the hash id when present + valid + in the pending list.
 * - Falls through to the first pending id otherwise (already-decided,
 *   invalid hash, no hash, etc.).
 * - Returns null when the pending list is empty.
 */
export function pickInitialApprovalSelection(
  pendingIds: readonly string[],
  hash: string,
): string | null {
  const hashId = parseApprovalHash(hash);
  if (hashId && pendingIds.includes(hashId)) return hashId;
  return pendingIds[0] ?? null;
}

/**
 * Resolve the next selection when the pending list OR the URL hash
 * changes. Three-tier precedence — the order is the contract:
 *   1. Explicit, in-list hash id  → always wins (palette / bell / Discord)
 *   2. Current selection if still in pending list → keep it
 *   3. Fall through to first pending row
 *
 * Returns null only when (a) the pending list is empty AND
 * (b) the hash doesn't resolve.
 *
 * The "explicit hash always wins" branch is what makes the in-app
 * palette / bell deep-link work even when the user is already on
 * /inbox with another row selected (R1 reviewer BLOCKER B1).
 */
export function resolveInboxSelection(
  current: string | null,
  pendingIds: readonly string[],
  hash: string,
): string | null {
  const hashId = parseApprovalHash(hash);
  if (hashId && pendingIds.includes(hashId)) return hashId;
  if (current && pendingIds.includes(current)) return current;
  return pendingIds[0] ?? null;
}
